import { z } from 'zod/v4';

import { PartOriginRuntype } from '../part/partOriginTypes';

export interface ManufacturerDTO extends z.infer<typeof ManufacturerDTORuntype> {}
export const ManufacturerDTORuntype = z.object({
    alternative_names: z.array(z.string()),
    id: z.string(),
    name: z.string(),
    origin: PartOriginRuntype.nullable(),
});

export interface ManufacturerLiteDTO extends z.infer<typeof ManufacturerLiteDTORuntype> {}
export const ManufacturerLiteDTORuntype = z.object({
    id: z.string(),
    name: z.string(),
});

export interface ManufacturerWithPartCountDTO extends z.infer<typeof ManufacturerWithPartCountDTORuntype> {}
export const ManufacturerWithPartCountDTORuntype = ManufacturerDTORuntype.extend({
    part_count: z.number(),
});

export interface ManufacturerLineCardDTO extends z.infer<typeof ManufacturerLineCardDTORuntype> {}

export const ManufacturerLineCardDTORuntype = z.object({
    manufacturer_id: z.string(),
    supported_by: z.array(z.string()),
});

export interface FindManufacturersQueryDTO extends z.infer<typeof FindManufacturersQueryDTORuntype> {}
export const FindManufacturersQueryDTORuntype = z.object({
    type: z.literal('name'),
    value: z.string(),
});

export const ManufacturerMatchMethodDTORuntype = z.union([
    z.literal('exact'),
    z.literal('normalized'),
    z.literal('prefix'),
]);
export type ManufacturerMatchMethodDTO = z.infer<typeof ManufacturerMatchMethodDTORuntype>;

export const ManufacturerMatchedFieldDTORuntype = z.union([
    z.object({ type: z.literal('name') }),
    z.object({ type: z.literal('alternative_name'), value: z.string() }),
]);
export type ManufacturerMatchedFieldDTO = z.infer<typeof ManufacturerMatchedFieldDTORuntype>;

export const ManufacturerMatchReasonDTORuntype = z.object({
    method: ManufacturerMatchMethodDTORuntype,
    matched_field: ManufacturerMatchedFieldDTORuntype,
});
export type ManufacturerMatchReasonDTO = z.infer<typeof ManufacturerMatchReasonDTORuntype>;

export interface ManufacturerMatchDTO extends z.infer<typeof ManufacturerMatchDTORuntype> {}
export const ManufacturerMatchDTORuntype = z.object({
    manufacturer: ManufacturerDTORuntype.pick({ id: true, name: true }),
    match_reason: ManufacturerMatchReasonDTORuntype,
});

export interface FindManufacturersResultDTO extends z.infer<typeof FindManufacturersResultDTORuntype> {}
export const FindManufacturersResultDTORuntype = z.object({
    query: FindManufacturersQueryDTORuntype,
    matches: z.array(ManufacturerMatchDTORuntype),
});

export interface FindManufacturersResponseDTO extends z.infer<typeof FindManufacturersResponseDTORuntype> {}
export const FindManufacturersResponseDTORuntype = z.object({
    results: z.array(FindManufacturersResultDTORuntype),
});

export const ImportManufacturerErrorDetailRuntype = z.discriminatedUnion('type', [
    z.object({ type: z.literal('empty_name') }),
    z.object({ type: z.literal('empty_normalized_name') }),
    z.object({ type: z.literal('name_too_long'), max: z.number() }),
    z.object({
        type: z.literal('duplicate_name'),
        matches: z.array(
            z.object({
                query_name: z.string(),
                manufacturer_id: z.string(),
                manufacturer_name: z.string(),
                match_method: ManufacturerMatchMethodDTORuntype,
                matched_field: ManufacturerMatchedFieldDTORuntype,
            }),
        ),
    }),
    z.object({ type: z.literal('internal') }),
]);
export type ImportManufacturerErrorDetailDTO = z.infer<typeof ImportManufacturerErrorDetailRuntype>;

/**
 * Per-item outcome of a bulk manufacturer import. `name` echoes the `name` of
 * the corresponding request item so that response items can be related to
 * request items without relying on implicit indices.
 */
export const ImportManufacturerOutcomeRuntype = z.discriminatedUnion('status', [
    z.object({
        status: z.literal('created'),
        name: z.string(),
        manufacturer: ManufacturerLiteDTORuntype,
    }),
    z.object({
        status: z.literal('error'),
        name: z.string(),
        error: ImportManufacturerErrorDetailRuntype,
        message: z.string(),
    }),
]);
export type ImportManufacturerOutcomeDTO = z.infer<typeof ImportManufacturerOutcomeRuntype>;
